// City planner: turns every building region of the tile map into authored-style blocks for blocks.ts.
// Hand-authored blocks in city-art.json are kept as they are; everything else is split greedily into building
// rectangles, then into lots with a district style, a façade kind, roofs and seeded rooftop equipment.
// Deterministic (hash-seeded), presentation only: it reads the map, never changes it.
import { CITY_ART, CITY_STYLES as STYLES, WORLD, type CityStyle as Style } from '../../common/src/data';
import { GameMap, T, TILE, tileAt, districtAt, inTileRect } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import type { Block } from './blocks';

type Lot = Block['lots'][number];
/** Footprint of each roof prop in px (with its shadow), used to keep generated props inside their cell. */
const SIZE: Record<string, [number, number]> = { ac: [18, 15], tank: [24, 25], hut: [29, 27], vent: [12, 10], dish: [12, 12], skylight: [26, 16], hood: [22, 16],
  cooler: [25, 25], mast: [32, 14], cage: [35, 27], heli: [44, 44], laundry: [24, 12], tarp: [22, 16] };

const pick = <V>(list: readonly V[], r: number) => list[Math.min(list.length - 1, Math.floor(r * list.length))];
const walkable = (t: T) => t === T.SIDEWALK || t === T.PLAZA || t === T.ALLEY || t === T.ROAD || t === T.BOLLARD;

function styleAt(tx: number, ty: number): Style {
  const d = CITY_ART.districts.find(d => inTileRect(tx, ty, d));
  return STYLES[d?.style ?? CITY_ART.defaultStyle];
}

/** Signs on the street become façade hints for the building whose south face they sit in front of. */
const HINTS = [
  ...[...WORLD.signs, ...CITY_ART.neon.signs]
    .map(s => ({ x: Math.floor(s.x), y: Math.floor(s.y), front: (CITY_ART.signFronts as Record<string, string>)[s.text], reach: 2 })),
  // the clinic counter stands in its courtyard, a few tiles in front of the building
  ...WORLD.pois.filter(p => p.kind === 'clinic').map(p => ({ x: Math.floor(p.x), y: Math.floor(p.y), front: 'clinic', reach: 4 })),
].filter(h => h.front);

function roofProps(w: number, h: number, step: number | undefined, style: Style, seed: number): Lot['props'] {
  const out: Lot['props'] = [], sy = step ? step * TILE : -999;
  for (let cy = 12; cy + 30 < h - 4; cy += 36) for (let cx = 10; cx + 30 < w - 6; cx += 36) {
    if (cy + 30 > sy - 12 && cy < sy + 10) continue; // keep clear of the tall section's wall band
    const r = hash2(seed + cx, cy);
    if (r > style.density) continue;
    const kind = pick(style.props, hash2(cy, seed + cx)), [pw, ph] = SIZE[kind];
    out.push([kind, cx + Math.floor(hash2(cx, cy + seed) * (34 - pw)), cy + Math.floor(hash2(cx + seed, cy) * (34 - ph))]);
  }
  // one pipe run between the first two units, routed along their top edges
  const units = out.filter(p => p[0] === 'ac') as [string, number, number][];
  if (units.length >= 2 && hash2(seed, 99) < 0.6) {
    const [[, ax, ay], [, bx, by]] = units;
    out.push(['duct', Math.min(ax, bx) + 8, ay + 16, Math.max(ax, bx) + 8, by + 16]);
  }
  return out;
}

function planBlock(m: GameMap, x: number, y: number, w: number, h: number): Block {
  const style = styleAt(x + w / 2, y + h / 2), seed = x * 131 + y * 17;
  let open = 0; for (let tx = x; tx < x + w; tx++) if (walkable(tileAt(m, tx, y + h))) open++;
  const facade = open * 2 >= w && h >= 4 && w >= 4 && y + h < m.h ? 3 : 0;
  const roofRows = h - facade, lots: Lot[] = [];
  // the original styles keep their 5-9 tile lots and 4-tile sliver rule; others never leave a lot below lotMin
  const lotMin = style.lotMin ?? 5, lotMax = style.lotMax ?? 9, sliver = style.lotMin ?? 4;
  for (let lx = x, i = 0; lx < x + w; i++) {
    let lw = lotMin + Math.floor(hash2(seed + i, 7) * (lotMax - lotMin + 1));
    if (x + w - (lx + lw) < sliver) lw = x + w - lx; // never leave a sliver
    const hint = facade ? HINTS.find(hn => hn.x >= lx && hn.x < lx + lw && hn.y >= y + h - 1 && hn.y <= y + h + hn.reach) : undefined;
    let front = hint?.front ?? pick(style.fronts, hash2(lx, y + i));
    // neighbours differ: step to the next kind in the style's list
    for (let n = 0; !hint && n < style.fronts.length && lots.length && lots[lots.length - 1].front === front; n++) front = style.fronts[(style.fronts.indexOf(front) + 1) % style.fronts.length];
    const roof = pick(style.roofs, hash2(lx, seed)), step = roofRows >= 9 ? Math.floor(roofRows * (0.35 + hash2(lx, 5) * 0.2)) : roofRows >= 5 ? 2 : undefined;
    lots.push({ x: lx, w: lw, front, roof, step, lowRoof: style.lowRoof, props: roofProps(lw * TILE, roofRows * TILE, step, style, seed + lx) });
    lx += lw;
  }
  return { id: `gen-${x}-${y}`, x, y, w, h, facade, lots };
}

/** All blocks to paint: authored ones first, then generated rectangles covering every other building tile. */
export function planCity(m: GameMap): Block[] {
  const used = new Uint8Array(m.w * m.h), blocks: Block[] = [...CITY_ART.blocks];
  const mark = (x: number, y: number, w: number, h: number) => { for (let j = y; j < y + h; j++) used.fill(1, j * m.w + x, j * m.w + x + w); };
  for (const b of CITY_ART.blocks) mark(b.x, b.y, b.w, b.h);
  // rectangles never cross a district edge, so adding or reshaping one district can't replan another's blocks
  let home = WORLD.districts[0];
  const free = (x: number, y: number) => x < m.w && y < m.h && m.tiles[y * m.w + x] === T.BUILDING && !used[y * m.w + x] && inTileRect(x, y, home);
  for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) {
    home = districtAt(x, y);
    if (!free(x, y)) continue;
    let w = 0; while (free(x + w, y)) w++;
    let h = 1; while ([...Array(w).keys()].every(i => free(x + i, y + h))) h++;
    mark(x, y, w, h);
    if (w >= 3 && h >= 3) blocks.push(planBlock(m, x, y, w, h));
  }
  return blocks;
}
